import { FormDialogMode, useNotify, UserPermission } from '@globaleris/ui.library'
import { makeStyles, useTheme } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { InvoiceSummaryModel } from '../../../api/Argentia/endpoints/invoices/model/invoices'
import { LeadSummaryModel } from '../../../api/Argentia/endpoints/leads/model/leads'
import { ProcedureSummaryModel } from '../../../api/Argentia/endpoints/procedures'
import { TaskCommentModel } from '../../../api/Argentia/endpoints/tasks/model/tasks'
import { EnumDominioTarea } from '../../../api/oData/enums/enumDominioTarea'
import Page from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { GenericCustomFormDialog } from '../../../components/shared/genericCustomFormDialog/genericCustomFormDialog'
import {
  FormStateConfig,
  InputFieldConfig
} from '../../../components/shared/genericCustomFormDialog/genericCustomFormDialog.model'
import { ObjectionsCard } from '../../../components/shared/objectionsCard/objectionsCard'
import { ProcedureDetailDialog } from '../../../components/shared/procedureDetailDialog/procedureDetailDialog'
import useProcedures from '../../../components/shared/proceduresTable/proceduresTable.hooks'
import {
  getResourcesUserPermissions,
  hasPermission,
  hasRole
} from '../../../helpers/permissionsHelper'
import { useConfirmationDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { UserRoles } from '../../../model/enums/userRoles'
import { Resources } from '../../../model/RBAC/constants'
import { getActiveUser } from '../../../redux'
import { ObjectionsModel, ObjectionTableModel } from '../../invoicesPage/invoicesPage.Model'
import { deleteObjection, fetchInvoiceDetails } from '../../invoicesPage/invoicesPage.service'
import { CommmentListCard } from '../components/commentListCard/commentListCard'
import { TaskActionsCard } from '../components/taskActionsCard/taskActionsCard'
import {
  TaskAddEditForm,
  TaskAddEditFormValidation
} from '../components/taskAddEditForm/taskAddEditForm'
import { TaskPageHeaderActions } from '../components/taskPageHeaderActions/taskPageHeaderActions'
import { TaskRelatedEntityCard } from '../components/taskRelatedEntityCard/taskRelatedEntityCard'
import {
  useFetchAtlasUsers,
  useFetchGlobalerisUsers,
  useGetObjectionsDescriptionList,
  useGetTaskDetails
} from '../hooks/tasks.hooks'
import { getTaskModelRelatedEntityValue } from '../task.helpers'
import { mapTaskEditFormModelToTaskPatchModel } from '../tasks.mapper'
import { TaskAddEditFormModel } from '../tasks.model'
import {
  createTaskComment,
  executeTaskActions,
  fetchObjectionsFromTaskEntity,
  fetchTaskComments,
  resolveObjections,
  updateTask
} from '../tasks.services'
import { styles } from '../tasks.styles'
import { invoiceDetailsInputFields } from './tasks.edit.model'

interface TaskEditPageProps {
  location?: {
    state: {
      task: TaskAddEditFormModel
      taskDomain: EnumDominioTarea
    }
  }
}

const initialFormStateConfig: FormStateConfig = {
  formMode: FormDialogMode.ADD,
  open: false,
  inputFields: [] as InputFieldConfig[],
  title: '',
  okBtnLabel: 'cERRAR'
}

export const MAX_CHAR = 1000

export const TaskEditPage: React.FC<TaskEditPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'tasks', 'enums'])
  const history = useHistory()
  const { info, error } = useNotify()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  //Task data Prop
  const task = props.location?.state.task
  const taskDomain = props.location?.state.taskDomain
  if (!task || !taskDomain) {
    error(t('tasks:fetchErrors.taskEdit'))
    history.goBack()
    return <></>
  }
  const taskId = task.id
  const {
    taskDetails,
    objections: objectionInitialValue,
    relatedEntityCodeOptions,
    isFetching: isFetchingTaskDetails,
    taskRelatedEntity
  } = useGetTaskDetails(taskId, taskDomain, (e: string) => {
    error(t(e))
  })

  const { isFetching, objDescriptionList } = useGetObjectionsDescriptionList(
    taskDomain,
    (errorMsg: string) => {
      error(t(errorMsg))
    }
  )
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const formRef = useRef<TaskAddEditFormValidation>(null)
  const [formData, setFormData] = useState<TaskAddEditFormModel>(task as TaskAddEditFormModel) // related>EntityCode would be added after fetching the taskId
  const [procedureDetailDialogIsOpen, setProcedureDetailDialogIsOpen] = useState<boolean>(false)
  const [taskComments, setTaskComments] = useState<TaskCommentModel[]>([])
  const [objections, setObjections] = useState<ObjectionsModel[]>(objectionInitialValue ?? [])
  const [filteredObjections, setFilteredObjections] = useState<ObjectionsModel[]>(
    objectionInitialValue ?? []
  )
  const [isFetchingObjections, setIsFetchingObjections] = useState<boolean>(false)

  //User info
  const activeUser = getActiveUser()
  const hasAtlasAdminRole = hasRole(UserRoles.ATLAS_ADMIN)
  const activeUserIsAlreadyAssigned = activeUser.name === task.assignedUser
  const [assignedActiveUser, setAssignedActiveUser] = useState<boolean>(false)
  const { globClientUsersOptions, isFetching: isFetchingUsers } = useFetchGlobalerisUsers(
    activeUser?.domain,
    (errorMsg: string) => {
      error(t(errorMsg))
    }
  )
  const { atlasUsersOptions, isFetching: isFetchingAtlasUsers } = useFetchAtlasUsers(
    (errorMsg: string) => {
      error(t(errorMsg))
    },
    hasAtlasAdminRole
  )

  //Comments info
  const [isFetchingComments, setIsFetchingComments] = useState<boolean>(false)

  // company info
  const imgUrl = `${process.env.PUBLIC_URL}/favicons/${activeUser.favicon}`

  // related entity info
  const {
    procedures,
    procedure,
    setProcedure,
    isFetchingProcedures,
    isFetchingProcedure,
    refreshProcedures
  } = useProcedures({})
  const [genericCustomFormDialogInputData, setGenericCustomFormDialogInputData] =
    useState<any>(null)
  const [customFormDialogStateConfig, setCustomFormDialogStateConfig] =
    useState<FormStateConfig>(initialFormStateConfig)

  // #region Handlers Starts

  // ------------------- Header Actions Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.push(NonAuthRoutes.Tasks)
  }

  const handleAssignUserClick = () => {
    setAssignedActiveUser(true)
  }

  const handleSave = async () => {
    const formHasMissingInputs = formRef.current?.validateForm()
    if (formHasMissingInputs) {
      error(t('tasks:editTask.form.incompleteForm'))
    } else {
      const request = mapTaskEditFormModelToTaskPatchModel(formData)
      await updateTask(formData.id, request).then(response => {
        if (response.success) {
          info(t('tasks:fetchResponse.saveTask.success'))
          history.push(NonAuthRoutes.Tasks, { forceRefreshFlag: true })
        } else {
          error(t('tasks:fetchResponse.saveTask.error'))
        }
      })
    }
  }

  // ------------------- Task Handlers -----------------------------//

  const handleFormDataChange = (updatedFormData: TaskAddEditFormModel) => {
    setFormData(updatedFormData)
  }

  // ------------------- Comments Handlers -----------------------------//

  const handleAddComment = textComment => {
    const request: TaskCommentModel = {
      comment: textComment,
      user: activeUser.name
    }
    createTaskComment(formData.id, request)
      .then(response => {
        setIsFetchingComments(true)
        if (response.success) {
          info(t('tasks:fetchResponse.createTaskComment.success'))
          return fetchTaskComments(formData.id)
        } else {
          error(t('tasks:fetchResponse.createTaskComment.error'))
          return Promise.reject()
        }
      })
      .then(getCommentsResponse => {
        if (getCommentsResponse.success && getCommentsResponse.data) {
          setFormData(prevState => ({
            ...prevState,
            comments: getCommentsResponse.data ?? []
          }))
          setTaskComments(getCommentsResponse.data)
        } else {
          error(t('tasks:fetchResponse.getTaskComments.error'))
        }
      })
      .finally(() => {
        setIsFetchingComments(false)
      })
  }

  // ------------------- Action Handlers -----------------------------//

  const handleResolveObjections = async (selectedIds: number[]) => {
    await resolveObjections({ objections: selectedIds }).then(response => {
      if (response.success) {
        info(t('tasks:fetchResponse.executeTaskActions.success'))
        refreshObjections()
      } else {
        error(t('tasks:fetchResponse.executeTaskActions.error'))
      }
    })
  }

  const handleExecuteAction = (taskId: number) => {
    triggerEventGridAction()
  }

  const handleRedirectToLeadAction = (taskId: number) => {
    let relatedEntityData = taskRelatedEntity as LeadSummaryModel
    history.push(NonAuthRoutes.LeadsFile, {
      leadId: relatedEntityData?.id
    })
  }

  // ------------------- Objections Handlers -----------------------------//
  const handleDeleteObjection = async (row: ObjectionTableModel) => {
    getConfirmation(ConfirmationDialogs.DELETE_OBJECTION).then(async () => {
      await deleteObjection(row.id!).then(response => {
        if (response.success) {
          info(t('tasks:fetchResponse.deleteObjection.success'))
          refreshObjections()
        } else {
          error(t('tasks:fetchResponse.deleteObjection.error'))
        }
      })
      closeConfirmation()
    })
  }

  // ------------------- Task Related Entity Card Handlers -----------------------------//

  const handleViewMoreClick = async () => {
    switch (taskDomain) {
      case EnumDominioTarea.Tramitacion:
        const procedure = taskRelatedEntity as ProcedureSummaryModel
        setProcedure(procedure?.id)
        setProcedureDetailDialogIsOpen(true)
        break

      case EnumDominioTarea.Factura:
      case EnumDominioTarea.PortalClientesFactura:
        const invoice = taskRelatedEntity as InvoiceSummaryModel
        const expand = true
        const response = await fetchInvoiceDetails(invoice.id, expand)
        if (!response.success) {
          error(t('tasks:fetchErrors.invoiceDetails'))
          return
        }
        setGenericCustomFormDialogInputData(response.data)
        setCustomFormDialogStateConfig({
          formMode: FormDialogMode.EDIT,
          open: true,
          okBtnLabel: t('tasks:editTask.genericCustomFormDialog.okBtnLabel'),
          inputFields: invoiceDetailsInputFields.map(f => ({
            ...f,
            label: t(f.label) // this is to convert string into translation. If use in model.ts translation file is not yet available on i18n
          }))
        })
        break

      case EnumDominioTarea.Documento:
      case EnumDominioTarea.Lead:
      case EnumDominioTarea.EjecucionCF:
      default:
        break
    }
  }

  // ------------------- Procedure Detail Dialog Handlers -----------------------------//

  const handleProcedureDetailDialogClose = () => {
    setProcedureDetailDialogIsOpen(false)
  }

  // ------------------- Generic Custom Form Dialog Handlers -----------------------------//

  const handleGenericCustomFormDialogClose = (formData: any) => {
    setCustomFormDialogStateConfig(initialFormStateConfig)
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const triggerEventGridAction = async () => {
    await executeTaskActions(taskId, {}).then(response => {
      if (response.success) {
        info(t('tasks:fetchResponse.executeTaskActions.success'))
      } else {
        error(t('tasks:fetchResponse.executeTaskActions.error'))
      }
    })
  }

  const refreshObjections = async () => {
    const relatedEntityData = taskDetails && getTaskModelRelatedEntityValue(taskDetails)
    if (relatedEntityData && relatedEntityData.length > 0) {
      const relatedEntityId = relatedEntityData[1]
      if (relatedEntityId) {
        setIsFetchingObjections(true)
        await fetchObjectionsFromTaskEntity(relatedEntityId, taskDomain)
          .then(response => {
            if (response.success && response.data) {
              setObjections(response.data as ObjectionsModel[])
              setFilteredObjections(
                response.data.filter(
                  objection => objection.taskTypeCode === taskDetails?.taskType?.code
                ) as ObjectionsModel[]
              )
            } else {
              error(t('tasks:fetchErrors.objections'))
            }
          })
          .finally(() => {
            setIsFetchingObjections(false)
          })
      }
    }
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    if (taskDetails) {
      setTaskComments(taskDetails.comments)
    }
  }, [taskDetails])

  useEffect(() => {
    if (objectionInitialValue) {
      setObjections(objectionInitialValue)
      setFilteredObjections(
        objectionInitialValue.filter(
          objection => objection.taskTypeCode === taskDetails?.taskType?.code
        )
      )
    }
  }, [objectionInitialValue])

  // #endregion Hooks Ends

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('tasks:editTask.title')}>
        <TaskPageHeaderActions
          activeUserIsAlreadyAssigned={activeUserIsAlreadyAssigned}
          assignedActiveUser={assignedActiveUser}
          onAssignUserClick={handleAssignUserClick}
          onSaveClick={handleSave}
          onBackButtonClick={handleBackButtonClick}
        />
      </PageHeader>
      <PageCenterColumn>
        <TaskAddEditForm
          ref={formRef}
          formMode={FormDialogMode.EDIT}
          taskData={{ ...formData } as TaskAddEditFormModel}
          maxChar={MAX_CHAR}
          isFetchingUsers={isFetchingUsers}
          isFetchingTaskDetails={isFetchingTaskDetails}
          globClientUsersOptions={globClientUsersOptions}
          atlasUsersOptions={atlasUsersOptions}
          relatedEntityCodeOptions={relatedEntityCodeOptions}
          isFetchingAtlasUsers={isFetchingAtlasUsers}
          hasAtlasAdminRole={hasAtlasAdminRole}
          onFormDataChange={handleFormDataChange}
          assignedActiveUser={assignedActiveUser}
          taskDetails={taskDetails}
        />

        <TaskRelatedEntityCard
          isLoading={isFetchingTaskDetails}
          taskDomain={taskDomain}
          taskRelatedEntity={taskRelatedEntity}
          show={taskRelatedEntity !== undefined}
          onViewMoreClick={handleViewMoreClick}
        />

        <TaskActionsCard
          isLoading={isFetchingTaskDetails || isFetchingObjections}
          objectionData={filteredObjections}
          data={taskDetails?.taskType?.actionList ?? []}
          procedureId={taskDetails?.procedure?.id}
          procedureState={taskDetails?.procedure?.state}
          procedureType={taskDetails?.procedure?.type!}
          procedureParameters={taskDetails?.procedure?.parameters!}
          commodity={taskDetails?.procedure?.cups?.commodity}
          taskId={taskId}
          onResolveObjectionsClick={handleResolveObjections}
          onExecuteActionClick={handleExecuteAction}
          onRedirectToLeadActionClick={handleRedirectToLeadAction}
        />

        <ObjectionsCard
          isLoading={isFetchingTaskDetails || isFetchingObjections}
          objectionsData={filteredObjections}
          allowInsert={false}
          hasEditPermission={
            hasPermission(Resources.InvoicesPage, [UserPermission.EDIT]) ||
            hasPermission(Resources.ComunicationsCenterFiles, [UserPermission.EDIT])
          }
          tableUserPermissions={getResourcesUserPermissions([
            Resources.InvoicesPage,
            Resources.ComunicationsCenterFiles
          ])}
          objDescriptions={objDescriptionList}
          onRowDelete={handleDeleteObjection}
        />

        <CommmentListCard
          isLoading={isFetchingTaskDetails || isFetchingComments}
          imgPath={imgUrl}
          comments={taskComments}
          onSaveComment={handleAddComment}
        />

        <ProcedureDetailDialog
          procedure={procedure}
          showFormDetail={procedureDetailDialogIsOpen}
          handleClose={handleProcedureDetailDialogClose}
        />

        <GenericCustomFormDialog
          title={t(`tasks:editTask.genericCustomFormDialog.title.${EnumDominioTarea[taskDomain]}`)}
          formStateConfig={customFormDialogStateConfig}
          onClose={handleGenericCustomFormDialogClose}
          data={genericCustomFormDialogInputData}
        />
      </PageCenterColumn>
    </Page>
  )
}
